import React, { useEffect, useRef, useState } from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  RadiusScale,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

export type BasketErrorToastContent = {
  message: string
  title: string
}

type BasketErrorToastProps = BasketErrorToastContent & {
  onDismiss: () => void
}

const DISMISS_ANIMATION_DURATION_MS = 220

const AnimatedToast = withCitrusTheme(Box, () => ({
  '@keyframes basketErrorToastSlideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(24px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes basketErrorToastSlideOut': {
    from: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    to: {
      opacity: 0,
      transform: 'translateY(24px)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none !important',
  },
}))

export const BasketErrorToast = ({ message, onDismiss, title }: BasketErrorToastProps) => {
  const [isDismissing, setIsDismissing] = useState(false)
  const dismissTimeout = useRef<NodeJS.Timeout | null>(null)

  useEffect(
    () => () => {
      if (dismissTimeout.current) clearTimeout(dismissTimeout.current)
    },
    [],
  )

  const dismiss = () => {
    if (isDismissing) return

    setIsDismissing(true)
    dismissTimeout.current = setTimeout(onDismiss, DISMISS_ANIMATION_DURATION_MS)
  }

  const handleDismissKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      dismiss()
    }
  }

  return (
    <AnimatedToast
      alignItems={AlignItems.FlexStart}
      aria-live="assertive"
      bg={Color.Error_50}
      borderColor={Color.Primary_900}
      borderRadius={4 as RadiusScale}
      borderStyle={BorderStyle.Solid}
      borderWidth={0.5}
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.12)"
      data-animation-state={isDismissing ? 'exiting' : 'entering'}
      data-testid="basket-error-toast"
      display={Display.Flex}
      gap={3}
      paddingH={3}
      paddingV={3}
      role="alert"
      style={{
        animation: isDismissing
          ? `basketErrorToastSlideOut ${DISMISS_ANIMATION_DURATION_MS}ms ease-in forwards`
          : 'basketErrorToastSlideIn 240ms cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
      width="100%"
    >
      <Icon name="error" size={6} variant={IconVariant.Error} />
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1} gap={1}>
        <Text color={Color.Primary_900} fontWeight={FontWeight.Bold}>
          {title}
        </Text>
        <Text color={Color.ColdGrey_800}>{message}</Text>
      </Box>
      <Box
        aria-label="Dismiss error notification"
        display={Display.Flex}
        onClick={dismiss}
        onKeyDown={handleDismissKeyDown}
        role="button"
        tabIndex={0}
      >
        <Icon name="close" size={5} />
      </Box>
    </AnimatedToast>
  )
}
